import { useCallback, useEffect, useMemo, useState } from "react"

import { useLocalStorage } from "@uidotdev/usehooks"
import { useLocation, useMatch, useNavigate } from "react-router-dom"

import { appCampaign } from "@l2r-front/l2r-app-campaign"
import { API_MODULE_DOCUMENT, APP_DOCUMENT, appDocument } from "@l2r-front/l2r-app-doc"
import { Trans, useTranslation } from "@l2r-front/l2r-i18n"
import { CampaignIcon, DescriptionIcon } from "@l2r-front/l2r-icons"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useGetAvailableSubnetworkForModule, useGetNetworkModules, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { API_MODULE_CAMPAIGN } from "@l2r-front/l2r-shared-campaign"
import { L2RPopover, Typography, useMediaQuery, useTheme } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { l2rApps } from "../../constants/l2rApps"
import { cleanInnerPath } from "../../utils/urlUtils"

import * as Styled from "./AppSelector.styled"

export const AppSelector = (props) => {
    const {
        className,
    } = props

    const { selectedNetwork } = useNetworksStateContext()
    const { selectedSubNetworkSlugs } = useNetworksStateContext()
    const { storeMapBoundingBox } = useMapDispatchContext()
    const navigate = useNavigate()
    const match = useMatch("/:selectedNetwork/:app/:module/*")
    const matchAppOnly = useMatch("/:selectedNetwork/:app/*")
    const location = useLocation()
    const urlApp = match?.params?.app || matchAppOnly?.params?.app
    const theme = useTheme()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"))

    const [lastConsultedModules] = useLocalStorage("lastConsultedModules", {})
    const [popoverInfos, setPopoverInfos] = useState(null)
    const { t } = useTranslation(I18N_NAMESPACE)
    const networkModules = useGetNetworkModules()

    const apps = useMemo(() => {
        return Object.values(l2rApps)
            .filter(app => app.label)
            .sort((a, b) => a.index - b.index)
            .map(app => {
                const appModulesSettings = Object.values(app.modules)
                const appModuleIds = appModulesSettings.map(m => m.id)
                const enabled = networkModules.some(module =>
                    appModuleIds.includes(module) ||
                    appModulesSettings.some(appModule =>
                        appModule?.enablersModules?.includes(module),
                    ),
                )
                return {
                    ...app,
                    enabled,
                }
            })
    }, [networkModules])

    const navigateToApp = useCallback((appPath) => {
        const app = Object.values(l2rApps).find(app => app.path === appPath)
        const cachedModuleId = lastConsultedModules?.[app.path]
        const cachedModule = app.modules[cachedModuleId]
        const enabledModules = appPath === APP_DOCUMENT ? [appDocument.modules.documents]
            : Object.values(app.modules)
                .sort((a, b) => a.index - b.index)
                .filter(module => {
                    const enabledModulesForNetwork = selectedNetwork?.children?.length ? selectedSubNetworkSlugs.map(slug => {
                        return selectedNetwork.children.find(child => child.slug === slug)
                    })?.reduce((acc, subNetwork) => {
                        return [...acc, ...(subNetwork.modules || [])]
                    }, []) : selectedNetwork.modules
                    if (module.mandatoryModules) {
                        return module?.mandatoryModules?.every(mandatoryModule => {
                            return enabledModulesForNetwork?.includes(mandatoryModule)
                        })
                    } else {
                        return module.alwaysEnabled || enabledModulesForNetwork.includes(module.id) || enabledModulesForNetwork.some(enabledModule => {
                            return module?.enablersModules?.includes(enabledModule)
                        })
                    }
                })
        const firstEnabledModule = (cachedModule && enabledModules.some(module => module.id === cachedModule.id)) ? cachedModule
            : enabledModules[0]
        const previousPath = match?.params?.["*"].length ? match?.params?.["*"] : ""
        const cleanedPreviousPath = cleanInnerPath(previousPath, match?.params?.app)
        const fullPath = `/${selectedNetwork.slug}/${app.path}/${firstEnabledModule?.path}/${cleanedPreviousPath}${location?.search ? location.search : ""}`
        navigate(fullPath)

    }, [selectedNetwork, location, match, lastConsultedModules, navigate, selectedSubNetworkSlugs])

    useEffect(() => {
        if (!urlApp) {
            const firstEnabledApp = apps.find(app => app?.enabled)
            if (firstEnabledApp?.path) {
                navigateToApp(firstEnabledApp.path)
            }
        }
    }, [urlApp, navigate, navigateToApp, apps])

    const handleAppChanged = useCallback((_, appPath) => {
        if (appPath && appPath !== urlApp) {
            storeMapBoundingBox()
            navigateToApp(appPath)
        }
    }, [navigateToApp, storeMapBoundingBox, urlApp])

    const handlePopoverOpen = useCallback((event, app) => {
        if (app.path === appDocument.path) {
            setPopoverInfos({
                anchor: event.currentTarget,
                content: t(app.i18nNamespace, "app.documents"),
            })

        } else if (app.path === appCampaign.path) {
            setPopoverInfos({
                anchor: event.currentTarget,
                content: t(app.i18nNamespace, "app.campaign"),
            })
        } else if (!app.enabled) {
            setPopoverInfos({
                anchor: event.currentTarget,
                content: <Trans namespace={I18N_NAMESPACE} i18nKey="containers.appSelector.appNotSubscribed">
                    "Module {{ appName: t(app.i18nNamespace, app.label) }} non souscrit "
                </Trans>,
            })
        }
    }, [t])

    const handlePopoverClose = useCallback(() => {
        setPopoverInfos(prev => ({
            ...prev,
            anchor: null,
        }))
    }, [])

    const hasSubNetworks = selectedNetwork.children?.length > 0
    const availableSubNetworksForDocument = useGetAvailableSubnetworkForModule(API_MODULE_DOCUMENT)?.length > 0
    const availableSubNetworksForCampaign = useGetAvailableSubnetworkForModule(API_MODULE_CAMPAIGN)?.length > 0

    const crossModulesButtonsConfig = [{
        app: appCampaign,
        icon: CampaignIcon,
        moduleAvailableForSubNetworks: availableSubNetworksForCampaign,
        moduleApiKey: API_MODULE_CAMPAIGN,
    }, {
        app: appDocument,
        icon: DescriptionIcon,
        moduleAvailableForSubNetworks: availableSubNetworksForDocument,
        moduleApiKey: API_MODULE_DOCUMENT,
    }]

    const selectedApp = useMemo(() => {
        return urlApp
    }, [urlApp])

    const open = Boolean(popoverInfos?.anchor)

    if (!urlApp) {
        return <div className={className} />
    }

    return (
        <Styled.Container
            className={className}
            value={selectedApp}
            onChange={handleAppChanged}
            disabled={!urlApp}
            exclusive>
            {
                apps.map(app => {
                    const Icon = app.icon
                    return <div
                        key={app.path}
                        onMouseEnter={(e) => handlePopoverOpen(e, app)}
                        onMouseLeave={handlePopoverClose}>
                        <Styled.AppButton
                            key={app.path}
                            value={app.path}
                            disabled={!app.enabled}
                        >
                            {app.icon && <Icon />}
                            {isLargeScreen && t(app.i18nNamespace, app.label)}
                        </Styled.AppButton>
                    </div>
                })
            }
            {
                (crossModulesButtonsConfig.some(config => {
                    return hasSubNetworks ? config.moduleAvailableForSubNetworks
                        : selectedNetwork?.modules?.includes(config.moduleApiKey)
                })) && <Styled.ModulesContainer>
                    {
                        crossModulesButtonsConfig.filter(config => {
                            return hasSubNetworks ? config.moduleAvailableForSubNetworks
                                : selectedNetwork?.modules?.includes(config.moduleApiKey)
                        }).map(config => {
                            const Icon = config.icon
                            return <div
                                key={config.app.path}
                                onMouseEnter={(e) => handlePopoverOpen(e, config.app)}
                                onMouseLeave={handlePopoverClose}>
                                <Styled.ModuleButton
                                    id={`${config.app.path}-module-button`}
                                    key={config.app.path}
                                    value={config.app.path}>
                                    {Icon && <Icon />}
                                </Styled.ModuleButton>
                            </div>
                        })
                    }
                </Styled.ModulesContainer>
            }
            <L2RPopover
                id="mouse-over-popover"
                open={open}
                anchorEl={popoverInfos?.anchor}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus>
                <Typography variant="Desktop & Mobile/Body/Small">
                    {popoverInfos?.content || ""}
                </Typography>
            </L2RPopover>
        </Styled.Container >
    )
}

